import { useEffect, useRef, useState } from 'react';
import { Rect, Line, Circle } from 'react-konva';
import { useTransformer } from './TransformerContext';
import TextBox from './TextBox';

const rotatePoint = (x, y, angle) => {
  const rad = (Math.PI / 180) * angle;
  return {
    x: x * Math.cos(rad) - y * Math.sin(rad),
    y: x * Math.sin(rad) + y * Math.cos(rad),
  };
};

const inverseRotatePoint = (x, y, angle) => {
  return rotatePoint(x, y, -angle);
};

const getClosestEdgeIndex = (clickX, clickY, points, rotation, shapeX, shapeY) => {
  let minDist = Infinity;
  let insertIndex = 0;
  for (let i = 0; i < points.length; i += 2) {
    const x1 = points[i];
    const y1 = points[i + 1];
    const x2 = points[(i + 2) % points.length];
    const y2 = points[(i + 3) % points.length];

    const p1 = rotatePoint(x1, y1, rotation);
    const p2 = rotatePoint(x2, y2, rotation);

    const abs1 = { x: shapeX + p1.x, y: shapeY + p1.y };
    const abs2 = { x: shapeX + p2.x, y: shapeY + p2.y };

    const A = clickX - abs1.x;
    const B = clickY - abs1.y;
    const C = abs2.x - abs1.x;
    const D = abs2.y - abs1.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    const param = lenSq !== 0 ? dot / lenSq : -1;

    let xx, yy;
    if (param < 0) {
      xx = abs1.x;
      yy = abs1.y;
    } else if (param > 1) {
      xx = abs2.x;
      yy = abs2.y;
    } else {
      xx = abs1.x + param * C;
      yy = abs1.y + param * D;
    }

    const dx = clickX - xx;
    const dy = clickY - yy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < minDist) {
      minDist = dist;
      insertIndex = (i + 2) % points.length;
    }
  }
  return insertIndex;
};

const BasicShapes = ({
  id,
  type,
  isSelected,
  onSelect,
  stroke = '#1890ff',
  draggable = true,
  fill = 'white',
  initialX = 100,
  initialY = 100,
  initialWidth = 100,
  initialHeight = 100,
  initialRadius = 60,
  strokeWidth = 2,
  points = [0, -50, 47.5, -15, 29.4, 40.5, -29.4, 40.5, -47.5, -15],
  opacity,
  ...props
}) => {
  const shapeRef = useRef(null);
  const transformerRef = useTransformer();

  const [shapePos, setShapePos] = useState({ x: initialX, y: initialY });
  const [rotation, setRotation] = useState(0);

  // center of shape (used for TextBox x/y)
  const [center, setCenter] = useState({ x: initialX, y: initialY });

  // bounding box size (used for TextBox width/height)
  const [boxSize, setBoxSize] = useState({
    width: initialWidth,
    height: initialHeight,
  });

  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  const [radius, setRadius] = useState(initialRadius);
  const [pointsState, setPointsState] = useState(
    type === 'polygon' ? [...points] : []
  );

  const { fontSize, fontStyle, align, textColor, fontFamily } = props;

  // Attach Transformer
  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected, transformerRef]);

  // Recompute center + bounding box whenever shape changes
  useEffect(() => {
    if (shapeRef.current) {
      const node = shapeRef.current;
      const box = node.getClientRect(); // works for rect, circle, polygon (incl. rotation)
      setCenter({
        x: box.x + box.width / 2,
        y: box.y + box.height / 2,
      });
      setBoxSize({
        width: box.width,
        height: box.height,
      });
    }
  }, [shapePos, width, height, radius, pointsState, rotation]);

  // Nudge to force transformend when font changes (so bbox recalcs)
  useEffect(() => {
    if (shapeRef.current) {
      shapeRef.current.scale({ x: 1.0000001, y: 1.0000001 });
      shapeRef.current.fire('transformend', { target: shapeRef.current }, true);
    }
  }, [fontSize, fontStyle]);

  const handleUpdate = () => {
    if (!shapeRef.current) return;
    const node = shapeRef.current;
    const { x, y } = node.position();
    setShapePos({ x, y });
    setRotation(node.rotation());
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    if (type === 'polygon' || type === 'triangle') {
      const newPoints = pointsState.map((val, i) =>
        i % 2 === 0 ? val * scaleX : val * scaleY
      );
      setPointsState(newPoints);
    } else if (type === 'rect' || type === 'rectangle') {
      setWidth(width * scaleX);
      setHeight(height * scaleY);
    } else if (type === 'circle') {
      setRadius(radius * scaleX);
    }

    node.scaleX(1);
    node.scaleY(1);
    handleUpdate(); // updates shapePos + rotation, then useEffect recomputes bbox
  };

  const handleAnchorDragMove = (index, e) => {
    const newPoints = [...pointsState];
    const absX = e.target.x() - shapePos.x;
    const absY = e.target.y() - shapePos.y;

    const local = inverseRotatePoint(absX, absY, rotation);
    newPoints[index * 2] = local.x;
    newPoints[index * 2 + 1] = local.y;
    setPointsState(newPoints);
  };

  const renderAnchors = () => {
    return pointsState.map((val, i) => {
      if (i % 2 !== 0) return null;
      const px = pointsState[i];
      const py = pointsState[i + 1];

      const rotated = rotatePoint(px, py, rotation);
      const x = shapePos.x + rotated.x;
      const y = shapePos.y + rotated.y;

      const pointIndex = i / 2;
      return (
        <Circle
          key={`anchor-${i}`}
          x={x}
          y={y}
          radius={6}
          fill="red"
          stroke="white"
          strokeWidth={1}
          draggable
          onDragMove={(e) => handleAnchorDragMove(pointIndex, e)}
        />
      );
    });
  };

  const handleAddPoint = (e) => {
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const insertIndex = getClosestEdgeIndex(
      pointer.x,
      pointer.y,
      pointsState,
      rotation,
      shapePos.x,
      shapePos.y
    );
    const local = inverseRotatePoint(pointer.x - shapePos.x, pointer.y - shapePos.y, rotation);
    const newPoints = [...pointsState];
    newPoints.splice(insertIndex, 0, local.x, local.y);
    setPointsState(newPoints);
  };

  const handleTextMouseDown = () => {
    if (shapeRef.current) {
      shapeRef.current.startDrag();
    }
  };

  // const commonProps = {
  //   ref: shapeRef,
  //   id,
  //   textId: id + '-text',
  //   fill,
  //   stroke,
  //   strokeWidth,
  //   draggable: isSelected && draggable,   
  //   onClick: (e) => { handleTransformEnd(); onSelect(e); },
  //   onTap: (e) => { handleTransformEnd(); onSelect(e); },
  //   onDragMove: handleUpdate,
  //   onTransformEnd: handleTransformEnd,
  //   x: shapePos.x,
  //   y: shapePos.y,
  //   rotation: rotation,
  //   opacity: opacity,
  // };

  const commonProps = {
    ref: shapeRef,
    id,
    textId: id + '-text',
    fill,
    stroke,
    strokeWidth,
    draggable,                            // ✅ always draggable if prop says so
    onClick: (e) => {
      e.cancelBubble = true;
      onSelect(e);                        // just select on click
    },
    onTap: (e) => {
      e.cancelBubble = true;
      onSelect(e);
    },
    onDragMove: handleUpdate,
    onTransformEnd: handleTransformEnd,   // only called after real transforms
    x: shapePos.x,
    y: shapePos.y,
    rotation: rotation,
    opacity: opacity,
  };

  const getShape = () => {
    switch (type) {
      case 'circle':
        return <Circle {...commonProps} radius={radius} />;
      case 'rect':
      case 'rectangle':
        return <Rect {...commonProps} width={width} height={height} />;
      default:
        return (
          <Line
            {...commonProps}
            points={pointsState}
            closed
            onDblClick={handleAddPoint}
          />
        );
    }
  };

  const textSelected = (e) => {
    // you can handle text selection here if needed
  };
  const syncShapeWithText = (shapeNode, textNode) => {
    const textRect = textNode.getClientRect();
    const shapeRect = shapeNode.getClientRect();

    // absolute center of text
    const textCenter = {
      x: textRect.x + textRect.width / 2,
      y: textRect.y + textRect.height / 2,
    };

    // shape offset
    const shapeOffset = {
      x: shapeNode.x() - shapeRect.x,
      y: shapeNode.y() - shapeRect.y,
    };

    // set new shape position to align centers
    shapeNode.position({
      x: textCenter.x - shapeRect.width / 2 + shapeOffset.x,
      y: textCenter.y - shapeRect.height / 2 + shapeOffset.y,
    });
  };

  return (
    <>
      {getShape()}



      <TextBox
        id={id}
        x={center.x}
        y={center.y}
        width={boxSize.width}          // 👈 match shape bbox width
        height={boxSize.height}        // 👈 match shape bbox height
        verticalAlign="middle"
        onSelect={textSelected}
        fontSize={fontSize}
        fontStyle={fontStyle}
        align={align || 'center'}
        textColor={textColor}
        fontFamily={fontFamily}
        onMouseDownOnText={handleTextMouseDown}
      />
      {(type === 'polygon' || type === 'triangle') && isSelected && renderAnchors()}
    </>
  );
};

export default BasicShapes;
